import { put, call, all, select, take } from 'redux-saga/effects';
import { matchRoutes } from 'react-router-config';
import { ALL_FOLDER_NAME, SCRAPS_FOLDER_NAME } from '@wix/da-url';
import {
  getIsLoggedIn,
  getPageSize,
  getPagingMode,
} from '@wix/da-shared-react/pkg/publicSession/selectors';
import { init as gruserInit } from '@wix/da-gruser-shared/pkg/redux/actions/';
import { getStreamIdsByGroupId } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { deleteStream } from '@wix/da-shared-react/pkg/Stream/base/actions';
import { flagEnabled } from '@wix/da-react-context/pkg/flags/redux/selectors';
import { purgeAllModules } from '../actions/modules';
import {
  fetchSection,
  fetchSectionSuccess,
  fetchSectionPrivateSuccess,
} from '../actions/sections';
import { routes, getSectionKeys } from '../routes';
import {
  DEVIATIONS_PER_FETCH_COUNT,
  FOLDER_DEVIATIONS_STREAM_GROUP_ID,
} from '../streams/folderDeviations';
import { PapiRequestDaUserProfileInitBackroom } from '@wix/da-papi-types';
import { PapiInitGallectionRequestParams } from '../../types/papi';
import { PostsType } from '../../types/posts';
import { FULLTEXT_JOURNALS_PER_FETCH, POSTS_PER_FETCH } from '../streams/posts';
import { DEFAULT_BACKROOM_SECTION } from '../../constants';
import { GroupNotesFolderType } from '../../types/backroomSection';
import { getCustomInitActionsForWidgets } from '../components/UserWidgets/redux/initActionsForWidgets';

import { isApiError, requestPuppy } from '@wix/da-http-client';
import { fetchModulesSuccess } from '@wix/da-gruser-shared/pkg/redux/actions/modules';

function* fetch(section, params) {
  return yield call(
    requestPuppy,
    {
      method: 'get',
      url: `init/${section}`,
      params: {
        ...params,
      },
    },
    undefined,
    'dauserprofile'
  );
}

// Sometimes, when switching sections, some streams will remain initialized
// which is not always desired. For instance, we need to refetch the folder
// deviations stream when switching between sections in order to get the
// discount information, since discount information is only returned for the
// single folder. If we do not destroy the streams, we will end up with stale
// data in the store.
function* destroySectionStreams() {
  const folderDeviationStreams = yield select(state =>
    getStreamIdsByGroupId(state, FOLDER_DEVIATIONS_STREAM_GROUP_ID)
  );
  // add other streams that need to be deleted when switching between sections
  const allStreamIds = [...folderDeviationStreams];
  yield all(allStreamIds.map(streamId => put(deleteStream(streamId))));
}

export default function* fetchSectionData(action) {
  const { location } = action;

  const routeData = matchRoutes(routes, location.pathname).pop();
  const sectionKey = routeData.route.key;
  const sectionKeys = getSectionKeys();

  if (!sectionKeys.includes(sectionKey)) {
    return;
  }

  yield put(fetchSection(sectionKey));

  const routeParams = routeData.match.params;

  let requestParams;

  switch (sectionKey) {
    case 'gallery':
    case 'favourites':
    case 'private_collections':
      const pageSize = yield select(getPageSize);
      const pagingMode = yield select(getPagingMode);
      const isPaginated = pagingMode === 'page';
      const isLoggedIn = yield select(getIsLoggedIn);
      const gallectionRequestParams: PapiInitGallectionRequestParams = {
        username: routeParams.username,
        with_subfolders: true,
        ...(isLoggedIn && {
          deviations_limit: isPaginated ? pageSize : DEVIATIONS_PER_FETCH_COUNT,
        }),
      };

      if (routeParams.folderId !== undefined) {
        gallectionRequestParams.folder_id = routeParams.folderId;
      } else {
        switch (routeParams.folderName) {
          case SCRAPS_FOLDER_NAME:
            gallectionRequestParams.scraps_folder = true;
            break;
          case ALL_FOLDER_NAME:
            gallectionRequestParams.all_folder = true;
            break;
          default:
        }
      }

      requestParams = gallectionRequestParams;
      break;
    case 'shop':
      requestParams = {
        username: routeParams.username,
        shop_section: routeParams.shopSection,
      };
      break;
    case 'posts':
      requestParams = {
        username: routeParams.username,
        posts_section: routeParams.postsSection,
        limit: FULLTEXT_JOURNALS_PER_FETCH,
        order: routeParams.sortOrder,
      };
      if (routeParams.postsSection === PostsType.POLLS) {
        requestParams.limit = POSTS_PER_FETCH;
      } else {
        requestParams.expand = 'deviation.fulltext';
      }
      break;
    case 'subscriptions':
      const isPostsTiers = yield select(state =>
        flagEnabled(state, 'posts_tiers')
      );
      requestParams = {
        username: routeParams.username,
        tier_deviationid: routeParams.tierDeviationid,
      };
      if (!isPostsTiers) {
        requestParams.section = routeParams.tierContentType;
      }
      break;
    case 'backroom':
      requestParams = {
        username: routeParams.username,
        backroom_section:
          routeParams.backroomSection || DEFAULT_BACKROOM_SECTION,
        folder: GroupNotesFolderType.INBOX,
        offset: 0,
      } as PapiRequestDaUserProfileInitBackroom;
      break;
    case 'adoptabobbles':
      requestParams = {
        username: routeParams.username,
      };
      break;
    default:
      requestParams = {
        username: routeParams.username,
      };
  }

  const result = yield call(fetch, sectionKey, requestParams);

  if (result && isApiError(result)) {
    if (result.errorCode === 302 && result.errorDetails?.redirectUrl) {
      window.location.replace(result.errorDetails.redirectUrl);
    }
  }

  if (result && !isApiError(result)) {
    yield put(purgeAllModules());
    yield call(destroySectionStreams);
    const { page: gruserPage, ...pureGruser } = result.gruser;
    yield put(gruserInit({ gruser: pureGruser, gruserPage }));
    yield take(fetchModulesSuccess);
    const customInitActionsForWidgets = getCustomInitActionsForWidgets({
      requestParams,
      initData: result,
      section: sectionKey,
    });
    yield all(customInitActionsForWidgets.map(initAction => put(initAction)));
    yield put(fetchSectionSuccess(sectionKey));
    yield put(
      fetchSectionPrivateSuccess(result.gruser?.page?.isPrivate || false)
    );
  }
}
